import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Star, BadgeCheck, MapPin, Clock, ArrowLeft, Heart } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DishCard, SectionHeader } from "@/components/Cards";
import { fetchChef, fetchChefs } from "@/lib/apiData";

export default function ChefProfile() {
  const { id } = useParams();
  const { data, isLoading } = useQuery({ queryKey: ["chef", id], queryFn: () => fetchChef(id), retry: false });
  const { data: allChefs = [] } = useQuery({ queryKey: ["chefs"], queryFn: () => fetchChefs() });
  const chef = data?.chef;
  const dishes = data?.dishes || [];

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="chef-loading">
        <div className="w-8 h-8 border-2 border-[#d00000] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!chef) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-32 text-center" data-testid="chef-not-found">
        <h1 className="font-display text-3xl text-neutral-900">Chef not found</h1>
        <Link to="/marketplace"><Button className="mt-6 bg-[#d00000] hover:bg-[#a30000] rounded-none">Back to marketplace</Button></Link>
      </div>
    );
  }

  const others = allChefs.filter((c) => c.id !== id).slice(0, 3);

  return (
    <div data-testid="chef-profile-page">
      {/* Cover */}
      <section className="relative h-[52vh] min-h-[380px]">
        <img src={chef.cover} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/20" />
        <div className="absolute top-6 left-0 right-0">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <Link to="/marketplace" className="inline-flex items-center gap-2 text-sm text-neutral-200 hover:text-white transition-colors" data-testid="back-link">
              <ArrowLeft className="w-4 h-4" /> Back
            </Link>
          </div>
        </div>
      </section>

      {/* Header */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 -mt-28 relative">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="flex flex-col md:flex-row gap-8 items-start">
          <img src={chef.img} alt={chef.name} className="w-40 h-40 object-cover border-4 border-[#fbf9f7] shadow-2xl" />
          <div className="flex-1 md:pt-24">
            <span className="text-xs uppercase tracking-[0.2em] text-[#d00000]">{chef.type === "restaurant" ? "Restaurant" : "Indie chef"} · {chef.cuisine}</span>
            <div className="flex items-center gap-3 mt-2">
              <h1 className="font-display text-4xl sm:text-5xl font-black text-neutral-900">{chef.name}</h1>
              {chef.verified && <BadgeCheck className="w-7 h-7 text-[#d00000]" />}
            </div>
            <p className="text-neutral-500 mt-2">{chef.kitchen}</p>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-4 text-sm text-neutral-700">
              <span className="flex items-center gap-1.5"><Star className="w-4 h-4 fill-[#f5a623] text-[#f5a623]" /> {chef.rating} <span className="text-neutral-400">({chef.reviews} reviews)</span></span>
              <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#d00000]" /> {chef.city}</span>
              <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-[#d00000]" /> {chef.years} yrs experience</span>
            </div>
          </div>
          <Button onClick={() => toast.success(`Following ${chef.name}`)} data-testid="follow-btn" className="md:mt-24 bg-[#d00000] hover:bg-[#a30000] text-white rounded-none gap-2">
            <Heart className="w-4 h-4" /> Follow
          </Button>
        </motion.div>

        {/* Bio + signature */}
        <div className="grid md:grid-cols-3 gap-8 mt-12">
          <div className="md:col-span-2">
            <p className="text-xs uppercase tracking-[0.25em] text-neutral-400 mb-3">The story</p>
            <p className="text-lg text-neutral-700 leading-relaxed">{chef.bio}</p>
          </div>
          <div className="bg-white border border-neutral-200 p-6">
            <p className="text-xs uppercase tracking-[0.25em] text-[#d00000] mb-2">Signature dish</p>
            <p className="font-display text-2xl font-bold text-neutral-900">{chef.signature}</p>
          </div>
        </div>
      </section>

      {/* Menu */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <SectionHeader label="On the menu" title={`${dishes.length} dishes from this kitchen`} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {dishes.map((d, i) => <DishCard key={d.id} dish={d} index={i} />)}
        </div>
      </section>

      {/* Others */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <SectionHeader label="Keep exploring" title="Other kitchens you'll love" />
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
          {others.map((c) => (
            <Link key={c.id} to={`/chef/${c.id}`} className="group relative block overflow-hidden aspect-video border border-neutral-200" data-testid={`related-chef-${c.id}`}>
              <img src={c.img} alt={c.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
              <div className="absolute bottom-0 p-5">
                <p className="text-xs text-[#f5a623] uppercase tracking-widest">{c.cuisine}</p>
                <p className="font-display text-xl font-bold text-white">{c.name}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
